/**
 * Product Detail Panel Styles
 *
 * Right-side slide-in overlay. Self-contained opinionated tokens
 * (--kso-*) for the same reasons as the cart drawer and checkout
 * modal — overlays float above the host page and benefit from a
 * predictable, coherent visual language regardless of WP theme.
 *
 * Honors: merchant accent (--ks-primary chain), fonts + radii via
 * host bridge. Everything else is opinionated.
 */

import { OVERLAY_TOKENS } from '../theme';

export const PRODUCT_DETAIL_STYLES = `
  .ks-detail-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.55);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    z-index: 99998;
    opacity: 0;
    transition: opacity 320ms ease;
  }

  .ks-detail-backdrop.ks-visible { opacity: 1; }

  .ks-detail-panel {
    ${OVERLAY_TOKENS}
    /* Panel-specific radius override */
    --kso-radius-md: 12px;
    --kso-radius-sm: 8px;
    --kso-radius-xs: 6px;

    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(520px, 100vw);
    background: var(--kso-surface);
    color: var(--kso-ink);
    font-family: var(--kso-font);
    z-index: 99999;
    transform: translateX(100%);
    transition: transform 380ms cubic-bezier(0.22, 0.61, 0.36, 1);
    display: flex;
    flex-direction: column;
    box-shadow: -8px 0 40px -8px rgba(17, 24, 39, 0.18);
    overflow: hidden;
  }

  .ks-detail-panel * { box-sizing: border-box; }

  .ks-detail-panel.ks-open { transform: translateX(0); }

  /* Header */
  .ks-detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid var(--kso-hairline);
    flex-shrink: 0;
  }

  .ks-detail-back {
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    color: var(--kso-ink-soft);
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: 500;
    font-family: inherit;
    padding: 6px 10px;
    margin-left: -10px;
    border-radius: var(--kso-radius-xs);
    transition: background 180ms ease, color 180ms ease;
  }

  .ks-detail-back:hover {
    background: var(--kso-surface-alt);
    color: var(--kso-ink);
  }

  .ks-detail-back svg {
    width: 16px;
    height: 16px;
    stroke-width: 1.75;
  }

  .ks-detail-close {
    background: none;
    border: none;
    color: var(--kso-muted);
    cursor: pointer;
    padding: 6px;
    margin-right: -6px;
    border-radius: 999px;
    transition: background 180ms ease, color 180ms ease;
  }

  .ks-detail-close:hover {
    background: var(--kso-hairline);
    color: var(--kso-ink);
  }

  .ks-detail-close svg {
    width: 18px;
    height: 18px;
    display: block;
  }

  /* Body (scrollable) */
  .ks-detail-body {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: thin;
    scrollbar-color: var(--kso-hairline-strong) transparent;
  }

  /* Image */
  .ks-detail-image-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    background: var(--kso-surface-alt);
    overflow: hidden;
  }

  .ks-detail-image-wrap img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .ks-detail-image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--kso-surface-alt);
    color: var(--kso-muted-2);
  }

  .ks-detail-image-placeholder svg {
    width: 56px;
    height: 56px;
    opacity: 0.5;
  }

  .ks-detail-placeholder-letter {
    font-family: var(--kso-font-display);
    font-size: 5rem;
    font-weight: 500;
    color: var(--kso-muted-2);
    line-height: 1;
    text-transform: uppercase;
    user-select: none;
  }

  .ks-detail-badge {
    position: absolute;
    top: 14px;
    left: 14px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--kso-accent-ink);
    background: var(--kso-accent);
  }

  .ks-detail-badge--sale {
    background: var(--kso-danger);
    color: #ffffff;
  }

  /* Thumbnails strip */
  .ks-detail-thumbnails {
    display: flex;
    gap: 8px;
    padding: 12px 20px;
    overflow-x: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--kso-hairline-strong) transparent;
  }

  .ks-detail-thumb {
    width: 56px;
    height: 56px;
    border-radius: var(--kso-radius-xs);
    object-fit: cover;
    cursor: pointer;
    border: 1.5px solid transparent;
    transition: border-color 180ms ease, opacity 180ms ease;
    flex-shrink: 0;
    background: var(--kso-surface-alt);
  }

  .ks-detail-thumb.ks-active { border-color: var(--kso-accent); }
  .ks-detail-thumb:hover {
    border-color: var(--kso-accent);
    opacity: 0.85;
  }

  /* Content */
  .ks-detail-content {
    padding: 20px;
  }

  .ks-detail-type {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--kso-accent);
    margin-bottom: 8px;
  }

  .ks-detail-title {
    font-family: var(--kso-font-display);
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 1.15;
    letter-spacing: -0.018em;
    margin: 0 0 10px;
    color: var(--kso-ink);
  }

  .ks-detail-rating {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 14px;
  }

  .ks-detail-stars svg {
    width: 14px;
    height: 14px;
    color: #e8a838;
  }

  .ks-detail-stars svg.ks-star--empty { color: var(--kso-hairline-strong); }

  .ks-detail-rating-count {
    font-size: 0.75rem;
    color: var(--kso-muted);
  }

  /* Price */
  .ks-detail-price-wrap {
    display: flex;
    align-items: baseline;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 18px;
  }

  .ks-detail-price {
    font-family: var(--kso-font-display);
    font-size: 1.75rem;
    font-weight: 500;
    font-variation-settings: 'opsz' 36;
    letter-spacing: -0.018em;
    color: var(--kso-ink);
    font-variant-numeric: tabular-nums;
  }

  .ks-detail-price-original {
    font-size: 0.9375rem;
    color: var(--kso-muted-2);
    text-decoration: line-through;
    font-variant-numeric: tabular-nums;
  }

  .ks-detail-price-discount {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--kso-danger);
    background: var(--kso-danger-bg);
    padding: 3px 10px;
    border-radius: 999px;
  }

  .ks-detail-desc {
    font-size: 0.9375rem;
    line-height: 1.6;
    color: var(--kso-ink-soft);
    margin-bottom: 22px;
  }

  /* Sections */
  .ks-detail-section { margin-bottom: 22px; }

  .ks-detail-section-label {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--kso-muted);
    margin-bottom: 10px;
  }

  /* Variant pills */
  .ks-detail-variants {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .ks-detail-variant-btn {
    padding: 9px 18px;
    border: 1px solid var(--kso-hairline-strong);
    border-radius: var(--kso-radius-xs);
    background: transparent;
    color: var(--kso-ink);
    font-size: 0.875rem;
    font-family: inherit;
    cursor: pointer;
    transition: border-color 180ms ease, color 180ms ease, background 180ms ease;
  }

  .ks-detail-variant-btn:hover {
    border-color: var(--kso-ink);
    color: var(--kso-ink);
  }

  .ks-detail-variant-btn.ks-selected {
    border-color: var(--kso-ink);
    background: var(--kso-ink);
    color: var(--kso-surface);
  }

  .ks-detail-variant-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    text-decoration: line-through;
  }

  /* Quantity stepper — pill form matches cart drawer */
  .ks-detail-quantity {
    display: inline-flex;
    align-items: center;
    border: 1px solid var(--kso-hairline-strong);
    border-radius: 999px;
    overflow: hidden;
    background: var(--kso-surface);
    height: 42px;
    width: fit-content;
  }

  .ks-detail-qty-btn {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: var(--kso-ink-soft);
    cursor: pointer;
    font-size: 1rem;
    font-family: inherit;
    transition: background 160ms ease, color 160ms ease;
  }

  .ks-detail-qty-btn:hover:not(:disabled) {
    background: var(--kso-surface-alt);
    color: var(--kso-ink);
  }

  .ks-detail-qty-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  .ks-detail-qty-value {
    min-width: 36px;
    text-align: center;
    font-size: 0.9375rem;
    font-weight: 500;
    color: var(--kso-ink);
    background: transparent;
    border: none;
    padding: 0 4px;
    font-variant-numeric: tabular-nums;
  }

  /* Footer CTA */
  .ks-detail-footer {
    padding: 16px 20px 18px;
    border-top: 1px solid var(--kso-hairline);
    background: var(--kso-surface-alt);
    flex-shrink: 0;
  }

  .ks-detail-add-btn {
    width: 100%;
    padding: 14px 22px;
    background: var(--kso-ink);
    color: var(--kso-accent-ink);
    border: none;
    border-radius: var(--kso-btn-radius);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-family: inherit;
    cursor: pointer;
    transition: background 240ms ease, transform 120ms ease, letter-spacing 240ms ease;
  }

  .ks-detail-add-btn:hover:not(:disabled) {
    background: var(--kso-accent);
    letter-spacing: 0.18em;
  }

  .ks-detail-add-btn:active:not(:disabled) { transform: scale(0.985); }

  .ks-detail-add-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .ks-detail-add-btn.ks-out-of-stock {
    background: var(--kso-hairline-strong);
    color: var(--kso-muted);
    opacity: 1;
  }

  .ks-detail-added {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px 22px;
    background: var(--kso-success);
    color: #ffffff;
    border: none;
    border-radius: var(--kso-btn-radius);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    animation: ks-detail-added-in 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  @keyframes ks-detail-added-in {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .ks-detail-added svg {
    width: 18px;
    height: 18px;
    stroke-width: 2.25;
  }

  /* Loading / skeleton */
  .ks-detail-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 300px;
  }

  .ks-detail-skeleton-image {
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 0;
    background: var(--kso-surface-alt);
  }

  .ks-detail-skeleton-content {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .ks-detail-skeleton-content .ks-skeleton-text {
    border-radius: 4px;
    background: var(--kso-hairline);
  }

  /* Ticket selector (live events / consultations) */
  .ks-detail-tickets {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .ks-ticket-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px;
    border: 1px solid var(--kso-hairline);
    border-radius: var(--kso-radius-sm);
    gap: 16px;
    transition: border-color 180ms ease;
  }

  .ks-ticket-row:hover { border-color: var(--kso-hairline-strong); }

  .ks-ticket-row.ks-ticket--sold-out { opacity: 0.5; }

  .ks-ticket-info {
    flex: 1;
    min-width: 0;
  }

  .ks-ticket-name {
    font-family: var(--kso-font-display);
    font-size: 0.9375rem;
    font-weight: 500;
    color: var(--kso-ink);
    letter-spacing: -0.005em;
  }

  .ks-ticket-desc {
    font-size: 0.75rem;
    color: var(--kso-muted);
    margin-top: 2px;
    line-height: 1.45;
  }

  .ks-ticket-price {
    font-family: var(--kso-font-display);
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--kso-accent);
    margin-top: 4px;
    font-variant-numeric: tabular-nums;
  }

  .ks-ticket-qty {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  .ks-ticket-qty-value {
    min-width: 22px;
    text-align: center;
    font-weight: 500;
    font-size: 0.875rem;
    font-variant-numeric: tabular-nums;
  }

  .ks-ticket-sold-out {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--kso-danger);
  }

  .ks-ticket-total {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding-top: 14px;
    border-top: 1px solid var(--kso-hairline);
    margin-top: 4px;
  }

  .ks-ticket-total-label {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--kso-muted);
  }

  .ks-ticket-total-price {
    font-family: var(--kso-font-display);
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--kso-ink);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
  }

  @media (max-width: 520px) {
    .ks-detail-panel {
      width: 100vw;
    }
  }
`;
